import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import './AIMatchEstimator.css';

/* ══════════════════════════════════════════════════════════
   CONSTANTS — Sirf yahan update karo
══════════════════════════════════════════════════════════ */
const VENDOR_TYPES_AI = [
  { icon: '🎨', label: 'Decorator',    value: 'decorator' },
  { icon: '📸', label: 'Photographer', value: 'photographer' },
  { icon: '🌸', label: 'Mehendi',      value: 'mehendi_artist' },
  { icon: '🎵', label: 'DJ',           value: 'dj' },
  { icon: '💄', label: 'Makeup',       value: 'makeup_artist' },
  { icon: '🎂', label: 'Cake & Food',  value: 'cake_catering' },
  { icon: '⛺', label: 'Tent House',   value: 'tent_furniture' },
  { icon: '🎥', label: 'Videographer', value: 'videographer' },
  { icon: '🎤', label: 'Anchor',       value: 'anchor' },
  { icon: '🚗', label: 'Wedding Car',  value: 'wedding_car' },
];

const EVENT_TYPES = [
  { icon: '💍', label: 'Shaadi' },
  { icon: '🎂', label: 'Birthday' },
  { icon: '🏢', label: 'Corporate' },
  { icon: '👶', label: 'Baby Shower' },
  { icon: '💑', label: 'Anniversary' },
  { icon: '🎓', label: 'Graduation' },
  { icon: '🏠', label: 'Griha Pravesh' },
  { icon: '💒', label: 'Engagement' },
];

const CITIES = [
  'Raipur', 'Durg', 'Bilaspur', 'Korba', 'Rajnandgaon',
  'Jagdalpur', 'Ambikapur', 'Raigarh', 'Bhilai', 'Dhamtari'
];

const BUDGETS = [
  { label: '₹5k - ₹15k',  value: '5000-15000',   icon: '💵' },
  { label: '₹15k - ₹50k', value: '15000-50000',  icon: '💴' },
  { label: '₹50k - ₹1L',  value: '50000-100000', icon: '💶' },
  { label: '₹1L+',        value: '100000+',       icon: '💷' },
];

const GUESTS = [
  { label: 'Chota (< 50)',      value: '50' },
  { label: 'Medium (50-150)',   value: '150' },
  { label: 'Bada (150-300)',    value: '300' },
  { label: 'Bahut Bada (300+)', value: '500' },
];

// Vendor label for display
const getVendorLabel = (value) =>
  VENDOR_TYPES_AI.find(v => v.value === value)?.label || 'Vendor';

/* ══════════════════════════════════════════════════════════
   STEP INDICATOR
══════════════════════════════════════════════════════════ */
const StepDot = ({ step, current, label }) => (
  <div className={`step-dot-wrap ${current >= step ? 'done' : ''} ${current === step ? 'active' : ''}`}>
    <div className="step-dot">
      {current > step ? '✓' : step}
    </div>
    <span>{label}</span>
  </div>
);

/* ══════════════════════════════════════════════════════════
   RESULT CARD
══════════════════════════════════════════════════════════ */
const ResultCard = ({ result, vendorLabel, onExplore }) => (
  <div className="ai-result-card">

    {/* Price Estimate */}
    <div className="price-estimate-box">
      <div className="pe-header">
        <span className="pe-icon">💰</span>
        <div>
          <h4>AI Price Estimate</h4>
          <p>Aapke event ke liye recommended budget</p>
        </div>
      </div>
      <div className="pe-range">
        <span className="pe-min">₹{result.priceMin}</span>
        <div className="pe-bar">
          <div className="pe-fill" />
        </div>
        <span className="pe-max">₹{result.priceMax}</span>
      </div>
      <div className="pe-tags">
        {result.includes?.map((item, i) => (
          <span key={i} className="pe-tag">✅ {item}</span>
        ))}
      </div>
      {result.tip && (
        <div className="pe-tip">💡 {result.tip}</div>
      )}
    </div>

    {/* Vendor Match */}
    <div className="decorator-match-box">
      <h4>🎯 Best Match {vendorLabel}s</h4>
      <p className="dm-subtitle">Aapke budget aur event ke liye top picks</p>
      <div className="dm-list">
        {result.decorators?.map((d, i) => (
          <div key={i} className={`dm-card rank-${i + 1}`}>
            <div className="dm-rank">#{i + 1}</div>
            <div className="dm-info">
              <strong>{d.name}</strong>
              <span>{d.reason}</span>
              <div className="dm-meta">
                <span>⭐ {d.rating}</span>
                <span>📍 {d.city}</span>
                <span>💼 {d.experience} yrs</span>
              </div>
            </div>
            <div className="dm-price">{d.priceRange}</div>
          </div>
        ))}
      </div>
    </div>

    {/* CTA */}
    <button className="ai-explore-btn" onClick={onExplore}>
      🔍 Saare {vendorLabel}s Dekho
      <span className="btn-arrow">→</span>
    </button>
  </div>
);

/* ══════════════════════════════════════════════════════════
   MAIN COMPONENT
══════════════════════════════════════════════════════════ */
export default function AIMatchEstimator() {
  const navigate = useNavigate();
  const [step, setStep]       = useState(1);
  const [loading, setLoading] = useState(false);
  const [result, setResult]   = useState(null);
  const [error, setError]     = useState(null);

  const [form, setForm] = useState({
    vendorType: 'decorator', // default
    eventType:  '',
    city:       '',
    budget:     '',
    guests:     '',
    date:       '',
  });

  const isStepValid = () => {
    if (step === 1) return form.vendorType && form.eventType && form.city;
    if (step === 2) return form.budget && form.guests;
    return true;
  };

  /* ── AI CALL ─────────────────────────────────────────── */
  const callAI = useCallback(async () => {
    const apiKey = process.env.REACT_APP_GEMINI_API_KEY;
    if (!apiKey) throw new Error('API key missing');

    const vendorLabel = getVendorLabel(form.vendorType);

    const prompt = `User ek ${vendorLabel} dhundh raha hai DecorHub platform pe.

Event Details:
- Vendor Type: ${vendorLabel}
- Event Type: ${form.eventType}
- City: ${form.city}, Chhattisgarh
- Budget Range: ₹${form.budget}
- Expected Guests: ${form.guests}
- Event Date: ${form.date || 'Not specified'}

Tujhe JSON format mein response dena hai — kuch aur mat likho, sirf JSON:

{
  "priceMin": "XX,XXX",
  "priceMax": "XX,XXX",
  "includes": ["item1", "item2", "item3", "item4"],
  "tip": "ek helpful money-saving tip for ${vendorLabel} booking",
  "decorators": [
    {
      "name": "${vendorLabel} Business Name (realistic Chhattisgarh wala)",
      "reason": "Kyun best match hai (1 line)",
      "rating": "4.X",
      "city": "${form.city}",
      "experience": "X",
      "priceRange": "₹XX,XXX - ₹XX,XXX"
    },
    { },
    { }
  ]
}

Rules:
- priceMin/Max realistic hona chahiye given budget ${form.budget} for a ${vendorLabel}
- includes mein jo actually ${form.eventType} mein ${vendorLabel} provide karta hai woh likho
- 3 ${vendorLabel} suggestions do — realistic Indian/CG names
- tip helpful aur money-saving hona chahiye specifically for ${vendorLabel}
- Sirf valid JSON do, koi extra text nahi`;

    const res = await fetch('https://api.groq.com/openai/v1/chat/completions', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${apiKey}`,
      },
      body: JSON.stringify({
        model: 'meta-llama/llama-4-scout-17b-16e-instruct',
        messages: [
          {
            role: 'system',
            content: `Tu ek event industry expert hai India ka, specifically ${vendorLabel} services ke baare mein. Hamesha valid JSON return kar, koi extra text nahi.`,
          },
          { role: 'user', content: prompt },
        ],
        max_tokens: 800,
        temperature: 0.6,
      }),
    });

    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.error?.message || 'AI error');
    }

    const data = await res.json();
    let text = data.choices?.[0]?.message?.content || '';
    text = text.replace(/```json/g, '').replace(/```/g, '').trim();
    return JSON.parse(text);
  }, [form]);

  /* ── HANDLERS ─────────────────────────────────────────── */
  const handleSubmit = async () => {
    setLoading(true);
    setError(null);
    try {
      const aiResult = await callAI();
      setResult(aiResult);
      setStep(4);
    } catch (err) {
      console.error('AI error:', err);
      setError('AI se response nahi aaya — dobara try karo! 🙏');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setStep(1);
    setResult(null);
    setError(null);
    setForm({ vendorType: 'decorator', eventType: '', city: '', budget: '', guests: '', date: '' });
  };

  const handleExplore = () => {
    navigate(`/decorators?city=${form.city}&service=${form.eventType}&vendorType=${form.vendorType}`);
  };

  const vendorLabel = getVendorLabel(form.vendorType);

  /* ── RENDER ───────────────────────────────────────────── */
  return (
    <section className="ai-estimator-section">
      <div className="ai-estimator-container">

        {/* Section Header */}
        <div className="ai-section-header">
          <div className="ai-badge">✨ AI Powered</div>
          <h2>Apna Perfect {vendorLabel} Dhundho</h2>
          <p>Budget batao — AI best {vendorLabel}s suggest karega aur price estimate bhi dega!</p>
        </div>

        {/* Main Card */}
        <div className="ai-main-card">

          {/* Step Indicators */}
          {step < 4 && (
            <div className="step-indicators">
              <StepDot step={1} current={step} label="Vendor" />
              <div className={`step-line ${step > 1 ? 'done' : ''}`} />
              <StepDot step={2} current={step} label="Budget" />
              <div className={`step-line ${step > 2 ? 'done' : ''}`} />
              <StepDot step={3} current={step} label="Details" />
            </div>
          )}

          {/* ── STEP 1: Vendor Type + Event + City ── */}
          {step === 1 && (
            <div className="step-content">

              <h3>Kya chahiye? 🎪</h3>
              <div className="event-grid">
                {VENDOR_TYPES_AI.map(v => (
                  <button
                    key={v.value}
                    className={`event-btn ${form.vendorType === v.value ? 'selected' : ''}`}
                    onClick={() => setForm(f => ({ ...f, vendorType: v.value }))}
                  >
                    <span className="event-icon">{v.icon}</span>
                    <span>{v.label}</span>
                  </button>
                ))}
              </div>

              <h3 style={{ marginTop: '24px' }}>Kaisa event hai? 🎊</h3>
              <div className="event-grid">
                {EVENT_TYPES.map(e => (
                  <button
                    key={e.label}
                    className={`event-btn ${form.eventType === e.label ? 'selected' : ''}`}
                    onClick={() => setForm(f => ({ ...f, eventType: e.label }))}
                  >
                    <span className="event-icon">{e.icon}</span>
                    <span>{e.label}</span>
                  </button>
                ))}
              </div>

              <h3 style={{ marginTop: '24px' }}>Kaunsa city? 📍</h3>
              <div className="city-grid">
                {CITIES.map(city => (
                  <button
                    key={city}
                    className={`city-btn ${form.city === city ? 'selected' : ''}`}
                    onClick={() => setForm(f => ({ ...f, city }))}
                  >
                    {city}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* ── STEP 2: Budget + Guests ── */}
          {step === 2 && (
            <div className="step-content">
              <h3>Budget kitna hai? 💰</h3>
              <div className="budget-grid">
                {BUDGETS.map(b => (
                  <button
                    key={b.value}
                    className={`budget-btn ${form.budget === b.value ? 'selected' : ''}`}
                    onClick={() => setForm(f => ({ ...f, budget: b.value }))}
                  >
                    <span className="budget-icon">{b.icon}</span>
                    <span>{b.label}</span>
                  </button>
                ))}
              </div>

              <h3 style={{ marginTop: '24px' }}>Kitne guests? 👥</h3>
              <div className="guests-grid">
                {GUESTS.map(g => (
                  <button
                    key={g.value}
                    className={`guest-btn ${form.guests === g.value ? 'selected' : ''}`}
                    onClick={() => setForm(f => ({ ...f, guests: g.value }))}
                  >
                    {g.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* ── STEP 3: Date + Summary + Submit ── */}
          {step === 3 && (
            <div className="step-content">
              <h3>Event ki date? 📅 <span className="optional">(optional)</span></h3>
              <input
                type="date"
                className="date-input"
                value={form.date}
                min={new Date().toISOString().split('T')[0]}
                onChange={e => setForm(f => ({ ...f, date: e.target.value }))}
              />

              {/* Summary */}
              <div className="summary-box">
                <h4>📋 Summary</h4>
                <div className="summary-grid">
                  <div><span>Vendor</span>  <strong>{vendorLabel}</strong></div>
                  <div><span>Event</span>   <strong>{form.eventType}</strong></div>
                  <div><span>City</span>    <strong>{form.city}</strong></div>
                  <div><span>Budget</span>  <strong>₹{form.budget}</strong></div>
                  <div><span>Guests</span>  <strong>{form.guests}</strong></div>
                  {form.date && (
                    <div>
                      <span>Date</span>
                      <strong>{new Date(form.date).toLocaleDateString('hi-IN')}</strong>
                    </div>
                  )}
                </div>
              </div>

              {error && <div className="ai-error">{error}</div>}

              <button
                className="ai-submit-btn"
                onClick={handleSubmit}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <span className="spinner-small" />
                    AI analyze kar raha hai...
                  </>
                ) : (
                  <>✨ AI se Best {vendorLabel} Dhundho</>
                )}
              </button>
            </div>
          )}

          {/* ── STEP 4: Results ── */}
          {step === 4 && result && (
            <div className="step-content">
              <div className="result-header">
                <h3>🎯 Aapke liye best {vendorLabel}s!</h3>
                <button className="reset-btn" onClick={handleReset}>🔄 Dobara Try Karo</button>
              </div>
              <ResultCard
                result={result}
                vendorLabel={vendorLabel}
                onExplore={handleExplore}
              />
            </div>
          )}

          {/* Navigation Buttons */}
          {step < 3 && (
            <div className="step-nav">
              {step > 1 && (
                <button className="nav-btn back" onClick={() => setStep(s => s - 1)}>
                  ← Wapas
                </button>
              )}
              <button
                className={`nav-btn next ${!isStepValid() ? 'disabled' : ''}`}
                onClick={() => isStepValid() && setStep(s => s + 1)}
                disabled={!isStepValid()}
              >
                Aage →
              </button>
            </div>
          )}

          {step === 3 && !loading && (
            <div className="step-nav">
              <button className="nav-btn back" onClick={() => setStep(2)}>
                ← Wapas
              </button>
            </div>
          )}

        </div>
      </div>
    </section>
  );
}